import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import EmptyData from "go-core/components/EmptyData";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { CategoryApi, ItemApi } from "../../../../../../../../../services/Api/Organization/types";
import LiveOrdersBlockedItemsTableRowModal from "./LiveOrdersBlockedItemsTableRowModal";

interface Props {
	items: (ItemApi | CategoryApi)[];
	setItemToBlock: (item?: ItemApi | CategoryApi) => void;
	loading: boolean;
	handleUnblockItem: (item: ItemApi | CategoryApi) => Promise<void>;
	setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
}

const LiveOrdersBlockedItemsTableModal = ({
	items,
	setItemToBlock,
	loading,
	handleUnblockItem,
	setEditMode,
}: Props): JSX.Element => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const [loadingIndex, setLoadingIndex] = useState(-1);
	const confirmation = useConfirmation();

	const handleUnblock = async (item: ItemApi | CategoryApi, index: number) => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.action"),
			});
			setLoadingIndex(index);
			let res: ItemApi | CategoryApi;

			if ("category" in item) {
				res = await apiOrganization.unblockItem(item.id, {
					include: "category,item_group",
				});
			} else {
				res = await apiOrganization.unblockCategory(item.id);
			}
			await handleUnblockItem(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoadingIndex(-1);
	};

	const handleChangeSuspension = async (item: ItemApi | CategoryApi, index: number) => {
		if (item.suspended) {
			await handleUnblock(item, index);
		} else {
			setItemToBlock(item);
		}
	};

	const handleEditItem = (item: ItemApi | CategoryApi) => {
		setEditMode(true);
		setItemToBlock(item);
	};

	return (
		<table className="table table-preview live-orders-blocked-products-table">
			<thead>
				<tr>
					<th />
					<th className="action" />
				</tr>
			</thead>
			<tbody>
				{items.length > 0
					? items.map((item, index) => (
							<LiveOrdersBlockedItemsTableRowModal
								key={`item-${item.id}`}
								item={item}
								index={index}
								loadingIndex={loadingIndex}
								handleChangeSuspension={handleChangeSuspension}
								handleEditItem={handleEditItem}
							/>
					  ))
					: !loading && <EmptyData />}
			</tbody>
		</table>
	);
};

export default LiveOrdersBlockedItemsTableModal;
